<template>
  <div class="effect-configuration">
    <div class="section-header">
      <h4 class="gm-section-title">Skill Effects</h4>
      <button type="button" @click="addEffect" class="gm-btn gm-btn--sm">
        Add Effect
      </button>
    </div>

    <div v-if="effects.length === 0" class="gm-empty">
      <p class="gm-empty__hint">No effects defined. Click "Add Effect" to add skill effects.</p>
    </div>

    <div v-for="(effect, index) in effects" :key="index" class="gm-panel effect-item">
      <div class="effect-header">
        <h5 class="gm-section-title">Effect {{ index + 1 }}</h5>
        <button type="button" @click="removeEffect(index)" class="gm-btn gm-btn--sm gm-btn--danger">
          Remove Effect
        </button>
      </div>

      <div class="effect-fields">
        <!-- Basic Effect Properties -->
        <div class="form-row">
          <div class="gm-field">
            <label class="gm-label">Effect Name</label>
            <select v-model="effect.name" class="gm-select">
              <option :value="EffectEnum.None">None</option>
              <option :value="EffectEnum.KnockedOut">Knocked Out</option>
              <option :value="EffectEnum.Coma">Coma</option>
              <option :value="EffectEnum.Burning">Burning</option>
              <option :value="EffectEnum.Poisoned">Poisoned</option>
              <option :value="EffectEnum.Sleeping">Sleeping</option>
              <option :value="EffectEnum.Confused">Confused</option>
              <option :value="EffectEnum.Paralyzed">Paralyzed</option>
              <option :value="EffectEnum.Fear">Fear</option>
              <option :value="EffectEnum.Slowness">Slowness</option>
              <option :value="EffectEnum.Cold">Cold</option>
              <option :value="EffectEnum.Cursed">Cursed</option>
              <option :value="EffectEnum.Blindness">Blindness</option>
              <option :value="EffectEnum.Haste">Haste</option>
              <option :value="EffectEnum.Regeneration">Regeneration</option>
              <option :value="EffectEnum.Blessed">Blessed</option>
              <option :value="EffectEnum.ArcaneSurge">Arcane Surge</option>
              <option :value="EffectEnum.Marked">Marked</option>
            </select>
          </div>

          <div class="gm-field">
            <label class="gm-label">Base Chance</label>
            <input
              v-model.number="effect.base_chance"
              type="number"
              min="0"
              max="1"
              step="0.1"
              class="gm-input"
              placeholder="1.0"
            />
          </div>
        </div>

        <!-- Impact Configuration (Optional) -->
        <div class="subsection">
          <div class="subsection-header">
            <h6 class="gm-section-title">Effect Impact</h6>
            <label class="checkbox-label">
              <input
                type="checkbox"
                :checked="effect.impact !== null"
                @change="toggleImpact(effect, $event)"
              />
              Enable Impact
            </label>
          </div>

          <div v-if="effect.impact" class="impact-section">
            <div class="form-row">
              <div class="gm-field">
                <label class="gm-label">Impact Kind</label>
                <select v-model="effect.impact.kind" class="gm-select">
                  <option :value="ImpactTypeEnum.None">None</option>
                  <option :value="ImpactTypeEnum.KnockOut">Knock Out</option>
                  <option :value="ImpactTypeEnum.Damage">Damage</option>
                  <option :value="ImpactTypeEnum.Heal">Heal</option>
                  <option :value="ImpactTypeEnum.Shield">Shield</option>
                  <option :value="ImpactTypeEnum.Buff">Buff</option>
                  <option :value="ImpactTypeEnum.Debuff">Debuff</option>
                  <option :value="ImpactTypeEnum.Stun">Stun</option>
                  <option :value="ImpactTypeEnum.Sleep">Sleep</option>
                  <option :value="ImpactTypeEnum.Confusion">Confusion</option>
                  <option :value="ImpactTypeEnum.Paralysis">Paralysis</option>
                  <option :value="ImpactTypeEnum.Fear">Fear</option>
                  <option :value="ImpactTypeEnum.Freeze">Freeze</option>
                  <option :value="ImpactTypeEnum.Burn">Burn</option>
                  <option :value="ImpactTypeEnum.Poison">Poison</option>
                  <option :value="ImpactTypeEnum.Slow">Slow</option>
                  <option :value="ImpactTypeEnum.Haste">Haste</option>
                  <option :value="ImpactTypeEnum.Blind">Blind</option>
                  <option :value="ImpactTypeEnum.Silence">Silence</option>
                  <option :value="ImpactTypeEnum.Bleed">Bleed</option>
                  <option :value="ImpactTypeEnum.Disarm">Disarm</option>
                  <option :value="ImpactTypeEnum.Root">Root</option>
                  <option :value="ImpactTypeEnum.EnergyDecrease">Energy Decrease</option>
                  <option :value="ImpactTypeEnum.Reflect">Reflect</option>
                  <option :value="ImpactTypeEnum.Absorb">Absorb</option>
                  <option :value="ImpactTypeEnum.Dodge">Dodge</option>
                  <option :value="ImpactTypeEnum.Resist">Resist</option>
                  <option :value="ImpactTypeEnum.Immunity">Immunity</option>
                  <option :value="ImpactTypeEnum.Regeneration">Regeneration</option>
                  <option :value="ImpactTypeEnum.Lifesteal">Lifesteal</option>
                </select>
              </div>

              <div class="gm-field">
                <label class="gm-label">Violation Type</label>
                <select v-model="effect.impact.type" class="gm-select">
                  <option :value="ImpactViolationTypeEnum.None">None</option>
                  <option :value="ImpactViolationTypeEnum.Physical">Physical</option>
                  <option :value="ImpactViolationTypeEnum.Mental">Mental</option>
                  <option :value="ImpactViolationTypeEnum.Energy">Energy</option>
                  <option :value="ImpactViolationTypeEnum.Heat">Heat</option>
                  <option :value="ImpactViolationTypeEnum.Cold">Cold</option>
                  <option :value="ImpactViolationTypeEnum.Light">Light</option>
                  <option :value="ImpactViolationTypeEnum.Darkness">Darkness</option>
                </select>
              </div>
            </div>

            <FormulaBuilder
              v-model="effect.impact.formula"
              :title="`Effect ${index + 1} Impact Formula`"
            />
          </div>
        </div>

        <!-- Duration Modifier -->
        <div class="subsection">
          <h6 class="gm-section-title">Duration Modifier</h6>
          <div class="gm-field">
            <label class="gm-label">Label</label>
            <input
              v-model="effect.duration_modifier.label"
              type="text"
              class="gm-input"
              placeholder="Duration modifier label"
            />
          </div>

          <FormulaBuilder
            v-model="effect.duration_modifier.formula"
            :title="`Effect ${index + 1} Duration Formula`"
          />
        </div>

        <!-- Stat Modifiers -->
        <div class="subsection">
          <div class="subsection-header">
            <h6 class="gm-section-title">Stat Modifiers</h6>
            <button type="button" @click="addStatModifier(effect)" class="gm-btn gm-btn--sm">
              Add Stat Modifier
            </button>
          </div>

          <div v-if="!effect.stat_modifiers || effect.stat_modifiers.length === 0" class="gm-empty">
            <p class="gm-empty__hint">No stat modifiers. Click "Add Stat Modifier" to add modifiers.</p>
          </div>

          <div v-for="(modifier, modIndex) in effect.stat_modifiers" :key="modIndex" class="gm-panel modifier-item">
            <div class="modifier-header">
              <span class="gm-label">Stat Modifier {{ modIndex + 1 }}</span>
              <button type="button" @click="removeStatModifier(effect, modIndex)" class="gm-btn gm-btn--sm gm-btn--danger">
                Remove
              </button>
            </div>

            <div class="gm-field">
              <label class="gm-label">Label</label>
              <input
                v-model="modifier.label"
                type="text"
                class="gm-input"
                placeholder="Stat modifier label"
              />
            </div>

            <FormulaBuilder
              v-model="modifier.formula"
              :title="`Stat Modifier ${modIndex + 1} Formula`"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import {
  EffectEnum,
  ImpactTypeEnum,
  ImpactViolationTypeEnum,
  type AssignableEffectRequest,
  type ImpactRequest,
  type ModifierRequest,
  type FormulaRequest
} from '@/api/dx-backend'
import FormulaBuilder from './FormulaBuilder.vue'

// Props
interface Props {
  modelValue: AssignableEffectRequest[]
}

const props = defineProps<Props>()

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: AssignableEffectRequest[]]
}>()

// Reactive state
const effects = ref<AssignableEffectRequest[]>([...props.modelValue])

// Methods
const addEffect = () => {
  const newEffect: AssignableEffectRequest = {
    name: EffectEnum.None,
    impact: null,
    base_chance: 1,
    duration_modifier: {
      label: '',
      formula: {
        base: 0,
        requires: [],
        scaling: [],
        min_efficiency: null,
        max_efficiency: null
      }
    },
    stat_modifiers: []
  }
  effects.value.push(newEffect)
  emitUpdate()
}

const removeEffect = (index: number) => {
  effects.value.splice(index, 1)
  emitUpdate()
}

const toggleImpact = (effect: AssignableEffectRequest, event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.checked) {
    effect.impact = {
      kind: ImpactTypeEnum.None,
      type: ImpactViolationTypeEnum.None,
      formula: {
        base: 0,
        requires: [],
        scaling: [],
        min_efficiency: null,
        max_efficiency: null
      }
    }
  } else {
    effect.impact = null
  }
  emitUpdate()
}

const addStatModifier = (effect: AssignableEffectRequest) => {
  if (!effect.stat_modifiers) {
    effect.stat_modifiers = []
  }
  effect.stat_modifiers.push({
    label: '',
    formula: {
      base: 0,
      requires: [],
      scaling: [],
      min_efficiency: null,
      max_efficiency: null
    }
  })
  emitUpdate()
}

const removeStatModifier = (effect: AssignableEffectRequest, index: number) => {
  effect.stat_modifiers?.splice(index, 1)
  emitUpdate()
}

const emitUpdate = () => {
  emit('update:modelValue', [...effects.value])
}

// Watch for external changes
watch(() => props.modelValue, (newValue) => {
  effects.value = [...newValue]
}, { deep: true })
</script>

<style scoped>
.effect-configuration {
  margin-bottom: var(--gm-gap-lg);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gm-gap);
}

.effect-item {
  margin-bottom: var(--gm-gap);
}

.effect-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gm-gap);
}

.effect-fields {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap);
}

.subsection {
  background: var(--gm-surface-sunken);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  padding: var(--gm-gap-sm);
}

.subsection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gm-gap-sm);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-sm);
  color: var(--gm-text);
  font-size: 0.8rem;
  cursor: pointer;
}

.impact-section {
  margin-top: var(--gm-gap-sm);
}

.form-row {
  display: flex;
  gap: var(--gm-gap);
}

.form-row .gm-field {
  flex: 1;
}

.modifier-item {
  margin-bottom: var(--gm-gap-sm);
}

.modifier-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gm-gap-sm);
}
</style>
